import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { useAuth } from '@/state/auth';

export interface WebSocketMessage {
  type: string;
  tenant: string;
  key: string;
  eventName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item?: Record<string, any>;
}

interface WebSocketState {
  isConnected: boolean;
  send: (message: object) => void;
  subscribe: (handler: MessageHandler) => () => void;
}

export interface WebSocketOptions {
  /** Connect to the per-user composite partition (default true) */
  user?: boolean;
}

type MessageHandler = (message: WebSocketMessage) => void;

const WS_URL = import.meta.env.VITE_WS_URL || '';
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
// Pong deadline for the heartbeat watchdog. Backend contract: `{action:'ping'}`
// has no dedicated route and falls to the WS `$default` route, which replies
// `{"action":"pong"}` — that reply only reaches the client because of the
// `WebSocketDefaultRouteResponse` resource + `RouteResponseSelectionExpression:
// $default` in backend/template.yml (see the mirror comment there). If that
// RouteResponse disappears, no pong ever arrives and this watchdog would kill
// every healthy connection every HEARTBEAT_INTERVAL.
// Not below ~15s: a Lambda cold start on the pong path must not false-positive.
const PONG_DEADLINE = 20 * 1000; // 20 seconds

// --- Per-connection instance (external mutable state, not React state) ---

interface WSInstance {
  ws: WebSocket | null;
  handlers: Set<MessageHandler>;
  reconnectAttempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  heartbeatTimer: ReturnType<typeof setInterval> | null;
  pingDeadlineTimer: ReturnType<typeof setTimeout> | null;
  visibilityHandler: (() => void) | null;
  connectionCount: number;
  isConnected: boolean;
  connectionListeners: Set<() => void>;
  // Friend (impersonated account) of the most recent connect intent. The
  // friend-change effect reconnects only when this differs, so N consumers
  // re-rendering on one friend change trigger exactly one socket swap.
  friend: string;
  // Connect generation. Every teardown (forceTeardown, closeInstance) bumps
  // it; connectInstance captures it before `await getToken()` and aborts on
  // resume if it moved — a superseded connect can never open a second live
  // socket or resurrect one for a torn-down/unmounted instance (ENG-4511).
  generation: number;
  // ENG-4506: latched true on the FIRST successful open of this instance and
  // never reset (not by close, not by watchdog teardown, not by a generation
  // bump, not by impersonation reconnect). Poll gating reads this instead of
  // isConnected so a healthy reconnect flicker can't engage the fast no-WS
  // poll.
  hasEverConnected: boolean;
}

function createInstance(): WSInstance {
  return {
    ws: null,
    handlers: new Set(),
    reconnectAttempt: 0,
    reconnectTimer: null,
    heartbeatTimer: null,
    pingDeadlineTimer: null,
    visibilityHandler: null,
    connectionCount: 0,
    isConnected: false,
    connectionListeners: new Set(),
    friend: '',
    generation: 0,
    hasEverConnected: false,
  };
}

const instances = new Map<string, WSInstance>();

function getInstance(key: string): WSInstance {
  let inst = instances.get(key);
  if (!inst) {
    inst = createInstance();
    instances.set(key, inst);
  }
  return inst;
}

function notifyConnectionState(inst: WSInstance, nextState: boolean) {
  if (inst.isConnected === nextState) return;
  inst.isConnected = nextState;
  inst.connectionListeners.forEach(listener => listener());
}

function clearPingDeadline(inst: WSInstance) {
  if (inst.pingDeadlineTimer) {
    clearTimeout(inst.pingDeadlineTimer);
    inst.pingDeadlineTimer = null;
  }
}

// Arm the pong-deadline watchdog after sending a ping. The deadline MUST be
// cleared at exactly these four sites (missing any one is a bug):
//   (a) any inbound frame — top of onmessage;
//   (b) stopHeartbeat — covers onclose and closeInstance, so a deadline armed
//       just before the last consumer unmounts can't fire after teardown and
//       reconnect a socket with zero consumers;
//   (c) the watchdog's own expiry;
//   (d) the friend-change effect, which swaps sockets WITHOUT stopHeartbeat.
//   ((c) and (d) are covered by forceTeardown.)
// Accepted/benign behaviors:
// - Background-tab timer throttling delays detection only (clearTimeout and WS
//   message dispatch aren't throttled); worst case ≈ +1 min.
// - Sleep-resume with a deadline armed pre-sleep can force one spurious
//   reconnect of a healthy socket; it self-heals via the normal reconnect path.
// - One-way death (receive alive, send dead) is undetected by design — inbound
//   frames keep clearing the deadline.
function armPingDeadline(inst: WSInstance, onExpiry: () => void) {
  clearPingDeadline(inst);
  inst.pingDeadlineTimer = setTimeout(() => {
    inst.pingDeadlineTimer = null;
    onExpiry();
  }, PONG_DEADLINE);
}

function startHeartbeat(inst: WSInstance, onPongDeadlineExpiry: () => void) {
  stopHeartbeat(inst);
  inst.heartbeatTimer = setInterval(() => {
    if (inst.ws?.readyState === WebSocket.OPEN) {
      inst.ws.send(JSON.stringify({ action: 'ping' }));
      armPingDeadline(inst, onPongDeadlineExpiry);
    }
  }, HEARTBEAT_INTERVAL);
  // Ping immediately when the user returns to the tab (laptop sleep is the
  // common half-open producer), so a dead socket is detected in ~PONG_DEADLINE
  // instead of up to HEARTBEAT_INTERVAL. Reads inst.ws at fire time — never a
  // captured socket. Registered/unregistered symmetrically with the heartbeat:
  // startHeartbeat replaces any previous handler (via stopHeartbeat above), so
  // it can't leak or double-fire.
  inst.visibilityHandler = () => {
    if (
      document.visibilityState === 'visible' &&
      inst.ws?.readyState === WebSocket.OPEN
    ) {
      inst.ws.send(JSON.stringify({ action: 'ping' }));
      armPingDeadline(inst, onPongDeadlineExpiry);
    }
  };
  document.addEventListener('visibilitychange', inst.visibilityHandler);
}

function stopHeartbeat(inst: WSInstance) {
  if (inst.heartbeatTimer) {
    clearInterval(inst.heartbeatTimer);
    inst.heartbeatTimer = null;
  }
  if (inst.visibilityHandler) {
    document.removeEventListener('visibilitychange', inst.visibilityHandler);
    inst.visibilityHandler = null;
  }
  clearPingDeadline(inst); // deadline-clear site (b)
}

// Teardown-ONLY for a socket we are abandoning (watchdog expiry, friend-change
// effect); each caller supplies its own reconnect step. Bumps the connect
// generation, so it also aborts any connectInstance parked on `await
// getToken()` — invalidating in-flight connects is part of teardown, not
// reconnect policy. Nulling onclose before close() is MANDATORY: on a
// half-open socket close() parks in CLOSING for a browser-determined timeout
// while connectInstance's guard lets CLOSING pass, so the replacement
// connects; a stale onclose firing later against the shared instance state
// would flip the healthy replacement to disconnected, null the new inst.ws,
// and schedule a third connection.
function forceTeardown(inst: WSInstance) {
  inst.generation++; // abort any connect awaiting its token (ENG-4511)
  clearPingDeadline(inst); // deadline-clear sites (c) and (d)
  notifyConnectionState(inst, false);
  if (inst.ws) {
    inst.ws.onclose = null;
    inst.ws.close();
    inst.ws = null;
  }
}

// Imperative connect/reconnect operating on an instance directly.
// These are NOT hooks — they mutate the external singleton state.

function scheduleReconnect(
  inst: WSInstance,
  getToken: () => Promise<string>,
  friend: string,
  userPartition: boolean
) {
  if (inst.reconnectTimer) return;
  const delay =
    RECONNECT_DELAYS[
      Math.min(inst.reconnectAttempt, RECONNECT_DELAYS.length - 1)
    ];
  inst.reconnectAttempt++;
  inst.reconnectTimer = setTimeout(() => {
    inst.reconnectTimer = null;
    connectInstance(inst, getToken, friend, userPartition);
  }, delay);
}

async function connectInstance(
  inst: WSInstance,
  getToken: () => Promise<string>,
  friend: string,
  userPartition: boolean
) {
  if (
    !WS_URL ||
    inst.ws?.readyState === WebSocket.OPEN ||
    inst.ws?.readyState === WebSocket.CONNECTING
  ) {
    return;
  }

  // Record the partition target of this connect intent (the friend-change
  // effect dedupes against it) and capture the generation so the post-await
  // re-check can tell whether a teardown superseded this call mid-await.
  inst.friend = friend;
  const generation = inst.generation;

  try {
    const token = await getToken();
    // Post-await re-check (ENG-4511): the guard above is check-then-await, so
    // while we were parked on getToken a concurrent caller (pending reconnect
    // timer vs. friend-change effect) may have already connected, or a
    // teardown may have bumped the generation. Bail instead of assigning a
    // second live socket — the loser's orphaned socket would keep delivering
    // duplicate messages and its stray onclose would clobber shared state.
    if (
      generation !== inst.generation ||
      inst.ws?.readyState === WebSocket.OPEN ||
      inst.ws?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }
    if (!token) return;

    const url = new URL(WS_URL);
    url.searchParams.set('token', token);
    if (userPartition) {
      url.searchParams.set('user', 'true');
    }
    if (friend) {
      url.searchParams.set('account', friend);
    }

    const socket = new WebSocket(url.toString());
    inst.ws = socket;

    // Pong-deadline expiry: no inbound frame within PONG_DEADLINE of a ping —
    // the socket is half-open (NAT drop, LB idle-kill) even though readyState
    // is still OPEN. Force-teardown and reconnect. stopHeartbeat is NOT needed
    // here: the interval's send is readyState-guarded and startHeartbeat is
    // self-clearing on the next successful open.
    const onPongDeadlineExpiry = () => {
      forceTeardown(inst);
      scheduleReconnect(inst, getToken, friend, userPartition);
    };

    socket.onopen = () => {
      inst.reconnectAttempt = 0;
      inst.hasEverConnected = true;
      notifyConnectionState(inst, true);
      startHeartbeat(inst, onPongDeadlineExpiry);
    };

    socket.onmessage = event => {
      // Any inbound frame proves liveness — clear the pong deadline before
      // parsing; do not parse-match the pong specifically. (Clear site (a).)
      clearPingDeadline(inst);
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        inst.handlers.forEach(handler => handler(message));
      } catch {
        // Ignore genuinely non-JSON frames. (The pong IS valid JSON — it
        // parses fine, reaches handlers, and they filter it out.)
      }
    };

    socket.onclose = () => {
      notifyConnectionState(inst, false);
      stopHeartbeat(inst);
      inst.ws = null;
      scheduleReconnect(inst, getToken, friend, userPartition);
    };

    socket.onerror = () => {
      // onclose will fire after onerror
    };
  } catch {
    // A teardown mid-await owns the reconnect policy — a stale-generation
    // caller must not schedule a retry that captured the old friend.
    if (generation !== inst.generation) return;
    scheduleReconnect(inst, getToken, friend, userPartition);
  }
}

function closeInstance(inst: WSInstance) {
  // A connect parked on getToken must not outlive the last consumer — a stale
  // generation makes it bail on resume instead of opening a zombie socket
  // whose onclose→scheduleReconnect loop would keep it alive forever.
  inst.generation++;
  if (inst.reconnectTimer) {
    clearTimeout(inst.reconnectTimer);
    inst.reconnectTimer = null;
  }
  stopHeartbeat(inst);
  if (inst.ws) {
    inst.ws.onclose = null;
    inst.ws.close();
    inst.ws = null;
  }
  notifyConnectionState(inst, false);
}

// --- Hook ---

/**
 * Shared WebSocket hook. Connections are pooled by partition type:
 * - `{ user: true }` (default) → composite `{account}:{user}` partition
 * - `{ user: false }` → plain `{account}` partition
 *
 * Multiple hooks with the same options share one connection.
 */
export function useWebSocket(options?: WebSocketOptions): WebSocketState {
  const userPartition = options?.user ?? true;
  const instanceKey = userPartition ? 'user' : 'account';
  const inst = getInstance(instanceKey);

  const { getToken, friend } = useAuth();
  const getTokenRef = useRef(getToken);
  const friendRef = useRef(friend);
  const instRef = useRef(inst);

  useEffect(() => {
    getTokenRef.current = getToken;
    friendRef.current = friend;
    instRef.current = inst;
  });

  const isConnected = useSyncExternalStore(
    useCallback(
      (listener: () => void) => {
        inst.connectionListeners.add(listener);
        return () => {
          inst.connectionListeners.delete(listener);
        };
      },
      [inst]
    ),
    useCallback(() => inst.isConnected, [inst])
  );

  // Lifecycle: connect when first consumer mounts, close when last unmounts.
  useEffect(() => {
    const i = instRef.current;
    i.connectionCount++;
    if (i.connectionCount === 1 && WS_URL) {
      connectInstance(i, getTokenRef.current, friendRef.current, userPartition);
    }

    return () => {
      i.connectionCount--;
      if (i.connectionCount === 0) {
        closeInstance(i);
      }
    };
  }, [userPartition]);

  // Reconnect when the impersonated account changes. Gating on i.friend (the
  // last connect intent's target), NOT on i.ws: a live socket, a reconnect
  // backoff window (ws === null, timer pending) and an in-flight token fetch
  // all captured the OLD friend, so all three must be superseded or realtime
  // events keep flowing from the previous account's partition (ENG-4512).
  // The i.friend guard also makes this idempotent across the N consumers that
  // re-render on one friend change, and a no-op on consumer mount.
  useEffect(() => {
    const i = instRef.current;
    if (!WS_URL || i.connectionCount === 0 || i.friend === friend) return;
    if (i.reconnectTimer) {
      clearTimeout(i.reconnectTimer); // pending closure holds the old friend
      i.reconnectTimer = null;
    }
    forceTeardown(i); // also aborts an in-flight connect via generation bump
    i.reconnectAttempt = 0;
    connectInstance(i, getTokenRef.current, friendRef.current, userPartition);
  }, [friend, userPartition]);

  const send = useCallback((message: object) => {
    const i = instRef.current;
    if (i.ws?.readyState === WebSocket.OPEN) {
      i.ws.send(JSON.stringify(message));
    }
  }, []);

  const subscribe = useCallback((handler: MessageHandler) => {
    const i = instRef.current;
    i.handlers.add(handler);
    return () => {
      i.handlers.delete(handler);
    };
  }, []);

  return { isConnected, send, subscribe };
}

/**
 * ENG-4506: read-only WS liveness for poll gating.
 *
 * Deliberately NOT isConnected: isConnected flickers false during healthy
 * reconnects (onclose → backoff → onopen), and gating a 3s poll on it would
 * thrash. hasEverConnected latches on the first successful open, so the
 * fast-poll gate (!wsConfigured || !hasEverConnected) is true only when there
 * is no WS URL at all or the socket has never come up — the ticket's two
 * fallback conditions. A mid-session drop keeps the latch; that case is
 * covered by reconnect resync + the unconditional slow safety nets.
 *
 * Observes the singleton only: no auth, no connectionCount, never connects.
 * hasEverConnected can only flip during an isConnected false→true transition,
 * which already fires connectionListeners — so subscribing to the same
 * listener set needs no new notify path.
 */
export function useWsLiveness(options?: WebSocketOptions): {
  wsConfigured: boolean;
  hasEverConnected: boolean;
} {
  const userPartition = options?.user ?? true;
  const inst = getInstance(userPartition ? 'user' : 'account');
  const hasEverConnected = useSyncExternalStore(
    useCallback(
      (listener: () => void) => {
        inst.connectionListeners.add(listener);
        return () => {
          inst.connectionListeners.delete(listener);
        };
      },
      [inst]
    ),
    useCallback(() => inst.hasEverConnected, [inst])
  );
  return { wsConfigured: WS_URL !== '', hasEverConnected };
}
